import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import PaymentMethodSelector from '../components/Checkout/PaymentMethodSelector';
import CardSelector from '../components/Checkout/CardSelector';
import OrderSummary from '../components/Checkout/OrderSummary';
import StorePickupInfo from '../components/Checkout/StorePickupInfo';
import TipSelector from '../components/Checkout/TipSelector';
import { TiArrowBack } from "react-icons/ti";
import './CheckoutPage.css';

const CheckoutPage = ({ setCurrentPage, onOrderPlaced }) => {
  const { cartItems, selectedStore, getCartTotal, clearCart, addOrder } = useCart();
  const { user } = useAuth();

  const [currentStep, setCurrentStep] = useState('payment'); // 'payment', 'card', 'confirmation'
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const [tipPercentage, setTipPercentage] = useState(0.15);
  const [promoCode, setPromoCode] = useState('');
  const [orderNumber, setOrderNumber] = useState(Math.floor(100000 + Math.random() * 900000));

  // Calcular totales
  const subtotal = getCartTotal();
  const tipAmount = subtotal * tipPercentage;
  const totalAmount = subtotal + tipAmount;

  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
    if (method === 'card') {
      setCurrentStep('card');
    } else {
      // Para otros métodos, ir directamente a confirmación
      handlePlaceOrder();
    }
  };

  const handleCardSelect = (card) => {
    setSelectedCard(card);
    handlePlaceOrder();
  };

  const handlePlaceOrder = () => {
    // Crear objeto de orden
    const order = {
      id: orderNumber,
      items: cartItems,
      store: selectedStore,
      paymentMethod: selectedPaymentMethod,
      card: selectedCard,
      subtotal: subtotal,
      tip: tipAmount,
      total: totalAmount,
      status: 'confirmed',
      timestamp: new Date().toISOString(),
      user: user?.name || 'Guest',
      qrCode: `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=ORDER-${orderNumber}`
    };

    // Agregar al historial
    addOrder(order);

    // Limpiar carrito
    clearCart();
    // Notificar al App.jsx que la orden fue colocada
    if (onOrderPlaced) {
      onOrderPlaced(order);
    } else {
      // Ir a confirmación dentro de esta página
      setCurrentStep('confirmation');
    }
  };

  const handleBackToHome = () => {
    setCurrentPage('home');
  };

  const handleTrackOrder = () => {
    // Aquí iría la lógica para rastrear la orden
    alert(`Tracking order #${orderNumber}`);
  };

  // Renderizar paso actual
  const renderStep = () => {
    switch (currentStep) {
      case 'payment':
        return (
          <PaymentMethodSelector
            onPaymentSelect={handlePaymentMethodSelect}
            onBack={() => setCurrentPage('cart')}
          />
        );

      case 'card':
        return (
          <CardSelector
            onCardSelect={handleCardSelect}
            onBack={() => setCurrentStep('payment')}
          />
        );

      case 'confirmation':
        return (
          <div className="confirmation-step">
            <div className="confirmation-header">
              <div className="success-icon">✅</div>
              <h2>¡Su pago fue exitoso!</h2>
              <p className="order-number">Pedido #{orderNumber}</p>
            </div>

            <div className="confirmation-details">
              <StorePickupInfo store={selectedStore} />

              <div className="order-timeline">
                <h3>Estado del pedido</h3>
                <div className="timeline">
                  <div className="timeline-step active">
                    <div className="step-dot"></div>
                    <span className="step-label">Pedido recibido</span>
                  </div>
                  <div className="timeline-step">
                    <div className="step-dot"></div>
                    <span className="step-label">Preparando</span>
                  </div>
                  <div className="timeline-step">
                    <div className="step-dot"></div>
                    <span className="step-label">Listo para Pickup</span>
                  </div>
                </div>
              </div>

              <div className="confirmation-actions">
                <button
                  className="action-btn secondary"
                  onClick={handleTrackOrder}
                >
                  Seguimiento del pedido
                </button>
                <button
                  className="action-btn primary"
                  onClick={handleBackToHome}
                >
                  <TiArrowBack />
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <PaymentMethodSelector
            onPaymentSelect={handlePaymentMethodSelect}
            onBack={() => setCurrentPage('cart')}
          />
        );
    }
  };

  // Si no hay items en el carrito, mostrar mensaje
  if (cartItems.length === 0 && currentStep !== 'confirmation') {
    return (
      <div className="checkout-page empty">
        <h2>Tu carrito está vacío</h2>
        <p>Añade artículos al pago</p>
        <button
          className="back-to-menu-btn"
          onClick={() => setCurrentPage('menu')}
        >
          Browse Menu
        </button>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      {currentStep !== 'confirmation' && (
        <div className="checkout-header">
          <button
            className="back-button"
            onClick={() => currentStep === 'payment' ? setCurrentPage('cart') : setCurrentStep('payment')}
          >
            <TiArrowBack />
          </button>
          <h1>Checkout</h1>
          <div className="step-indicator">
            <span className={`step ${currentStep === 'payment' ? 'active' : ''}`}>1</span>
            <span className="step-line"></span>
            <span className={`step ${currentStep === 'card' ? 'active' : ''}`}>2</span>
            <span className="step-line"></span>
            <span className={`step ${currentStep === 'confirmation' ? 'active' : ''}`}>3</span>
          </div>
        </div>
      )}

      <div className="checkout-content">
        {currentStep === 'payment' && (
          <>
            <div className="checkout-left">
              {renderStep()}
            </div>

            <div className="checkout-right">
              <OrderSummary
                items={cartItems}
                subtotal={subtotal}
                tipPercentage={tipPercentage}
                tipAmount={tipAmount}
                totalAmount={totalAmount}
              />

              {selectedStore && (
                <StorePickupInfo store={selectedStore} />
              )}

              <TipSelector
                tipPercentage={tipPercentage}
                onTipChange={setTipPercentage}
              />

              <div className="promo-section">
                <input
                  type="text"
                  placeholder="Add promo code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  className="promo-input"
                />
                <button className="apply-promo-btn">Aplicar</button>
              </div>
            </div>
          </>
        )}

        {currentStep !== 'payment' && renderStep()}
      </div>
    </div>
  );
};

export default CheckoutPage;